import {
  identity,
  constant,
  emptyArray,
  emptyObject,
  mapArray,
  constantTrue,
  constantFalse,
  constantIdentity,
} from '@packages/utilities/fp/utils';
import {
  map as mapValues,
  keys as objectKeys,
} from '@packages/utilities/key-map';
import type {Json} from '@packages/utilities/json';
import {weakMemo} from '@packages/utilities/memo';
import {updateAt, append, prepend} from '@packages/utilities/array';
import {type ExpressionPath, ExpressionPathPart} from '../ExpressionPath';
import type {Type} from '../DataTypeDefinition';
import {matchExpression} from './GenericMatchExpression';
import {
  type Qualified,
  type TypeName,
  type Identifier,
} from '@packages/systems/wfdl-Qualified';
import {type Pair, getFirst, getSecond, pair} from '@packages/utilities/Pair';
import {type State} from '@packages/utilities/State';
import * as Variable from '../Variable';
import {
  type ExpressionF,
  type ElementF,
  type TypedF,
  _ESelect,
  _EExtend,
  _EElement,
  _ERecord,
  _EUnion,
  _ETyped,
  _EList,
  _ECase,
  _ECall,
  _EFunction,
  _EDistinct,
} from './GenericExpressionTypes';

export const _getTyped: <r>(arg1: ExpressionF<r>) => null | TypedF<r> =
  matchExpression({
    Typed: identity,
    _: (_) => null,
  });

export const _getTypedType: <r>(arg1: ExpressionF<r>) => null | Type =
  matchExpression({
    Typed: (val) => val.type,
    _: (_) => null,
  });

export const _getBoolean: <r>(arg1: ExpressionF<r>) => null | boolean =
  matchExpression({
    Boolean: identity,
    _: (_) => null,
  });

export const _getNumber: <r>(arg1: ExpressionF<r>) => null | number =
  matchExpression({
    Number: identity,
    _: (_) => null,
  });

export const _getText: <r>(arg1: ExpressionF<r>) => null | string =
  matchExpression({
    Text: identity,
    _: (_) => null,
  });

export const _getEnum: <r>(arg1: ExpressionF<r>) => null | string =
  matchExpression({
    Enum: identity,
    _: (_) => null,
  });

export const _getRecord: <r>(arg1: ExpressionF<r>) => null | {
  [key: string]: r;
} = matchExpression({
  Record: identity,
  _: (_) => null,
});

export const _getSelect: <r>(arg1: ExpressionF<r>) => null | {
  from: r;
  prop: string;
} = matchExpression({
  Select: identity,
  _: (_) => null,
});

export const _getExtend: <r>(arg1: ExpressionF<r>) => null | {
  record: r;
  prop: string;
  value: r;
} = matchExpression({
  Extend: identity,
  _: (_) => null,
});

export const _getUnion: <r>(arg1: ExpressionF<r>) => null | {
  tag: string;
  value: r;
} = matchExpression({
  Union: identity,
  _: (_) => null,
});

export const _getCase: <r>(arg1: ExpressionF<r>) => null | {
  value: r;
  cases: {
    [key: string]: r;
  };
} = matchExpression({
  Case: identity,
  _: (_) => null,
});

export const _getList: <r>(arg1: ExpressionF<r>) => null | Array<r> =
  matchExpression({
    List: identity,
    _: (_) => null,
  });

export const _getElement: <r>(arg1: ExpressionF<r>) => null | ElementF<r> =
  matchExpression({
    Element: identity,
    _: (_) => null,
  });

export const _getVariable: <r>(
  arg1: ExpressionF<r>
) => null | Variable.VariableType = matchExpression({
  Variable: identity,
  _: (_) => null,
});

export const _getCall: <r>(arg1: ExpressionF<r>) => null | {
  fun: r;
  arg: r;
} = matchExpression({
  Call: identity,
  _: (_) => null,
});

export const _getFunction: <r>(arg1: ExpressionF<r>) => null | {
  param: string;
  body: r;
} = matchExpression({
  Function: identity,
  _: (_) => null,
});

export const _getLiteral: <r>(arg1: ExpressionF<r>) => null | {
  name: Qualified<TypeName>;
  value: Json;
} = matchExpression({
  Literal: identity,
  _: (_) => null,
});

export const _getDistinct: <r>(arg1: ExpressionF<r>) => null | {
  name: Qualified<TypeName>;
  value: r;
} = matchExpression({
  Distinct: identity,
  _: (_) => null,
});

export const _getHole: <r>(
  arg1: ExpressionF<r>
) => null | Record<string, never> = matchExpression({
  Hole: identity,
  _: (_) => null,
});

// @ts-expect-error - TS7006 - Parameter 'part1' implicitly has an 'any' type. | TS7006 - Parameter 'part2' implicitly has an 'any' type.
const pathPartsEqual = (part1, part2) => {
  if (part1 === part2) return true;

  if (part1.in === 'Record' && part2.in === 'Record')
    return part1.at === part2.at;

  if (part1.in === 'Union' && part2.in === 'Union')
    return part1.tag === part2.tag;

  if (part1.in === 'Case' && part2.in === 'Case') {
    const at1 = part1.at;
    const at2 = part2.at;
    if (at1.type === 'value') return at2.type === 'value';
    if (at2.type === 'value') return false;
    return at1.val === at2.val;
  }

  if (part1.in === 'List' && part2.in === 'List')
    return part1.index === part2.index;

  if (part1.in === 'Element' && part2.in === 'Element') return true;

  if (part1.in === 'Call' && part2.in === 'Call') return part1.at === part2.at;

  if (part1.in === 'Function' && part2.in === 'Function') return true;

  return false;
};

export const _pathsEqual = (
  path1: ExpressionPath,
  path2: ExpressionPath
): boolean =>
  path1 === path2 ||
  (path1.length === path2.length &&
    path1.every((part, i) => pathPartsEqual(part, path2[i])));

const returnUndefined = (_: ExpressionF<never> | ExpressionF<unknown>) =>
  undefined;

const getNextExpression: <a>(
  expr: ExpressionF<a>
) => (k: ExpressionPathPart, p: ExpressionPath) => undefined | a =
  matchExpression({
    Record: (fields) => (key: ExpressionPathPart, _path: ExpressionPath) => {
      if (key.in !== 'Record') {
        return;
      }
      return fields[key.at];
    },
    Select:
      ({from}) =>
      (key: ExpressionPathPart, _path: ExpressionPath) => {
        if (key.in !== 'Select') {
          return;
        }
        return from;
      },
    Case:
      ({value, cases}) =>
      (key: ExpressionPathPart, _path: ExpressionPath) => {
        if (key.in !== 'Case') {
          return;
        }
        const {at} = key;

        if (at.type === 'value') {
          return value;
        }

        return cases[at.val];
      },
    Union: (union) => (key: ExpressionPathPart, _path: ExpressionPath) => {
      if (key.in !== 'Union') {
        return;
      }
      return union.value;
    },
    List: (list) => (key: ExpressionPathPart, _path: ExpressionPath) => {
      if (key.in !== 'List') {
        return;
      }
      const item = list[key.index];
      if (!item) {
        return;
      }
      return item;
    },
    Element: (el) => (key: ExpressionPathPart, _path: ExpressionPath) => {
      if (key.in !== 'Element') {
        return;
      }
      return el.data;
    },
    Call: (call) => (key: ExpressionPathPart, _path: ExpressionPath) => {
      if (key.in !== 'Call') {
        return;
      }
      return call[key.at];
    },
    Function: (fn) => (key: ExpressionPathPart, _path: ExpressionPath) => {
      if (key.in !== 'Function') {
        return;
      }
      const {body} = fn;
      return body;
    },
    Variable: (_) => (_key: ExpressionPathPart, path: ExpressionPath) => {
      throw new TypeError(
        `getIn: 'Variable' found before completing traversal of path (${JSON.stringify(
          path
        )}); 'Union' or 'Record' was expected.`
      );
    },
    Extend: (extend) => (key: ExpressionPathPart, _path: ExpressionPath) => {
      if (key.in !== 'Extend') {
        return;
      }

      const {record, value} = extend;

      if (key.at === 'value') {
        return value;
      }

      return record;
    },
    Typed: (typed) => (key: ExpressionPathPart, _path: ExpressionPath) => {
      if (key.in !== 'Typed') {
        return;
      }
      return typed.expr;
    },
    Distinct:
      (distinct) => (key: ExpressionPathPart, _path: ExpressionPath) => {
        if (key.in !== 'Distinct') {
          return;
        }
        return distinct.value;
      },
    _: constant((_key: ExpressionPathPart, _path: ExpressionPath) => undefined),
  });

/**
 * Provide the `Expression` at a given path.
 *
 * Example:
 *
 * ```js
 * const result = getIn(
 *   ERecord({
 *     foo: EUnion({
 *       tag: 'blah',
 *       value: ERecord({
 *         bar: EVariable(Variable.unqualified(identifier(data)))
 *       })
 *     })
 *   }),
 *   [
 *     {in: 'Record', at: 'foo'},
 *     {in: 'Union', tag: 'blah'},
 *     {in: 'Record', at: 'bar'},
 *   ]
 * );
 *
 * // result:
 * //
 * // EVariable(Variable.unqualified(identifier('data')))
 * ```
 */
export const _getIn = <a>(
  toExpression: (arg1: a) => ExpressionF<a>
): ((arg1: undefined | a, arg2: ExpressionPath) => undefined | a) => {
  return function getIn(
    bindingShape: a | undefined,
    path: ExpressionPath
  ): undefined | a {
    if (path.length === 0) {
      return bindingShape;
    }

    let curr: a | undefined = bindingShape;

    for (const key of path) {
      if (curr == null) {
        return undefined;
      }

      curr = getNextExpression(toExpression(curr))(key, path);
    }

    return curr;
  };
};

/**
 * Replaces an expression at the specified path.
 */
export const _setIn =
  <e>(
    onExpression: (
      arg1: (arg2: ExpressionF<e>) => ExpressionF<e>
    ) => (arg3: e) => e
  ) =>
  (expr: e, path: ExpressionPath, nextExpr: e) =>
    _updateIn(onExpression)(expr, path, () => nextExpr);

export const _updateIn = <e>(
  onExpression: (
    arg1: (arg2: ExpressionF<e>) => ExpressionF<e>
  ) => (arg3: e) => e
) => {
  const walk = (expr: e, path: ExpressionPath, updater: (arg14: e) => e): e => {
    if (path.length < 1) {
      return updater(expr);
    }

    const [key, ...nextPath] = path;

    return onExpression((innerExpr) =>
      matchExpression<e, ExpressionF<e>>({
        Record: (fields) => {
          // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
          if (key.in !== 'Record') {
            return innerExpr;
          }
          const {at} = key;
          const oldVal = fields[at];
          if (!oldVal) {
            return innerExpr;
          }
          const newVal = walk(oldVal, nextPath, updater);

          if (oldVal === newVal) {
            return innerExpr;
          }
          return _ERecord({
            ...fields,
            [at]: newVal,
          });
        },
        Case: ({value, cases}) => {
          // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
          if (key.in !== 'Case') {
            return innerExpr;
          }
          const {at} = key;

          if (at.type === 'value') {
            const newValue = walk(value, nextPath, updater);
            if (value === newValue) {
              return innerExpr;
            }
            return _ECase({value: newValue, cases});
          }

          const caseLabel = at.val;
          const oldHandler = cases[caseLabel];

          if (!oldHandler) {
            return innerExpr;
          }

          const newHandler = walk(oldHandler, nextPath, updater);

          if (oldHandler === newHandler) {
            return innerExpr;
          }

          return _ECase({
            value,
            cases: {
              ...cases,
              [caseLabel]: newHandler,
            },
          });
        },
        Union: (union) => {
          // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
          if (key.in !== 'Union') {
            return innerExpr;
          }
          const oldVal = union.value;
          const newVal = walk(oldVal, nextPath, updater);

          if (oldVal === newVal) {
            return innerExpr;
          }

          return _EUnion({
            tag: union.tag,
            value: newVal,
          });
        },
        List: (list) => {
          // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
          if (key.in !== 'List') {
            return innerExpr;
          }

          const {index} = key;

          const newList = updateAt(list, index, (item) =>
            walk(item, nextPath, updater)
          );

          if (list === newList) {
            return innerExpr;
          }

          return _EList(newList);
        },
        Element: (el) => {
          // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
          if (key.in !== 'Element') {
            return innerExpr;
          }
          const oldData = el.data;
          const newData = walk(oldData, nextPath, updater);

          if (oldData === newData) {
            return innerExpr;
          }

          return _EElement({
            id: el.id,
            type: el.type,
            data: newData,
            ...(el.meta && {meta: el.meta}),
          });
        },
        Call: (call) => {
          // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
          if (key.in !== 'Call') {
            return innerExpr;
          }
          const {fun, arg} = call;

          if (key.at === 'fun') {
            const newFun = walk(fun, nextPath, updater);

            if (fun === newFun) {
              return innerExpr;
            }

            return _ECall({
              fun: newFun,
              arg,
            });
          }

          const newArg = walk(arg, nextPath, updater);

          if (arg === newArg) {
            return innerExpr;
          }

          return _ECall({
            fun,
            arg: newArg,
          });
        },
        Function: (fn) => {
          // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
          if (key.in !== 'Function') {
            return innerExpr;
          }

          const {param, body} = fn;
          const newBody = walk(body, nextPath, updater);

          if (body === newBody) {
            return innerExpr;
          }

          return _EFunction({
            param,
            body: newBody,
          });
        },
        Extend: (extend) => {
          // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
          if (key.in !== 'Extend') {
            return innerExpr;
          }

          const {record, prop, value} = extend;

          if (key.at === 'record') {
            return _EExtend({
              prop,
              record: walk(record, nextPath, updater),
              value,
            });
          }

          return _EExtend({
            prop,
            record,
            value: walk(value, nextPath, updater),
          });
        },
        Typed: (typed) => {
          // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
          if (key.in !== 'Typed') {
            return innerExpr;
          }

          const newExpr = walk(typed.expr, nextPath, updater);

          if (typed.expr === newExpr) {
            return innerExpr;
          }

          return _ETyped({
            type: typed.type,
            expr: newExpr,
          });
        },
        Distinct: ({name, value}) => {
          // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
          if (key.in !== 'Distinct') {
            return innerExpr;
          }

          const newExpr = walk(value, nextPath, updater);

          if (value === newExpr) {
            return innerExpr;
          }

          return _EDistinct({
            name,
            value: newExpr,
          });
        },
        _: (_) => innerExpr,
      })(innerExpr)
    )(expr);
  };
  return walk;
};

export const _getStaticValue = <r>(
  expr: ExpressionF<r>
): undefined | Json | number | string | boolean =>
  matchExpression<r, undefined | Json | number | string | boolean>({
    Literal: (val) => val.value,
    Number: identity,
    Boolean: identity,
    Enum: identity,
    Text: identity,
    _: returnUndefined,
  })(expr);

/**
 * Retrieve an element's id
 */
export const _getElementId = <a>(el: ElementF<a>): string => {
  return el.id;
};

/**
 * Retrieve an element's data object. This is useful in order to properly support
 * `Typed` element data. Element `data` is also an Expression.
 *
 * _Example_
 *
 * ```jsx
 * // @wfdl-bin-link-only
 * const component = Component.create({
 *   render: EElement({
 *     id: 'some-id',
 *     type: qualify('Builtin', 'DOM'),
 *     data: ERecord({
 *       tag: EEnum('div')
 *     })
 *   })
 * })
 *
 * const element = getElementById(component.render, 'some-id');
 * const tag = getIn(
 *  getElementData(element),
 *  [{ in: 'Record', at: 'tag'}]
 * );
 *
 * console.log(tag);
 * ```
 * ```javascript
 * const tag = getIn(
 *  getElementData(element),
 *  [{ in: 'Record', at: 'tag'}]
 * );
 * ```
 */
export const _getElementData = <a>(el: ElementF<a>): a => el.data;

/**
 * Given an element and new element data, an updated element is returned.
 * This function is needed in order to support `Typed` element data.
 */
export const _setElementData = <a, b>(
  el: ElementF<a>,
  newData: b
): ElementF<b> => ({...el, data: newData});

export const map = weakMemo(<a, b>(fn: (x: a) => b) =>
  weakMemo(
    matchExpression<a, ExpressionF<b>>({
      Record: (oldProperties, original) => {
        const newProperties = mapValues(fn)(oldProperties);
        if ((newProperties as unknown) === oldProperties) {
          return original as unknown as ExpressionF<b>;
        }
        return _ERecord(newProperties);
      },
      Select: ({from, prop}, original) => {
        const newFrom = fn(from);
        if ((newFrom as unknown) === from) {
          return original as unknown as ExpressionF<b>;
        }
        return _ESelect({from: newFrom, prop});
      },
      Extend: ({record, prop, value}, original) => {
        const newRecord = fn(record);
        const newValue = fn(value);
        if (
          (newRecord as unknown) === record &&
          (newValue as unknown) === value
        ) {
          return original as unknown as ExpressionF<b>;
        }
        return _EExtend({record: newRecord, prop, value: newValue});
      },
      Union: ({tag, value}, original) => {
        const newValue = fn(value);
        if ((newValue as unknown) === value) {
          return original as unknown as ExpressionF<b>;
        }
        return _EUnion({tag, value: newValue});
      },
      Case: ({value, cases}, original) => {
        const newValue = fn(value);
        const newCases = mapValues(fn)(cases);
        if (
          (newValue as unknown) === value &&
          (newCases as unknown) === cases
        ) {
          return original as unknown as ExpressionF<b>;
        }
        return _ECase({value: newValue, cases: newCases});
      },
      List: (oldItems, original) => {
        const newItems = mapArray(fn)(oldItems);
        if ((newItems as unknown) === oldItems) {
          return original as unknown as ExpressionF<b>;
        }
        return _EList(newItems);
      },
      Element: (el, original) => {
        const oldData = _getElementData(el);
        const newData = fn(oldData);
        if ((newData as unknown) === oldData) {
          return original as unknown as ExpressionF<b>;
        }
        return _EElement(_setElementData(el, newData));
      },
      Call: ({fun, arg}, original) => {
        const newFun = fn(fun);
        const newArg = fn(arg);
        if ((newFun as unknown) === fun && (newArg as unknown) === arg) {
          return original as unknown as ExpressionF<b>;
        }
        return _ECall({fun: newFun, arg: newArg});
      },
      Function: ({param, body}, original) => {
        const newBody = fn(body);
        if ((newBody as unknown) === body) {
          return original as unknown as ExpressionF<b>;
        }
        return _EFunction({param, body: newBody});
      },
      Typed: (typed, original) => {
        const newExpr = fn(typed.expr);
        if ((newExpr as unknown) === typed.expr) {
          return original as unknown as ExpressionF<b>;
        }
        return _ETyped({type: typed.type, expr: newExpr});
      },
      Distinct: (distinct, original) => {
        const newExpr = fn(distinct.value);
        if ((newExpr as unknown) === distinct.value) {
          return original as unknown as ExpressionF<b>;
        }
        return _EDistinct({name: distinct.name, value: newExpr});
      },
      _: identity as (expr: ExpressionF<a>) => ExpressionF<b>,
    })
  )
);

/**
 * Runs a user provided updater function on every expression in the tree
 * and returns the updated tree.
 *
 * ```jsx
 * const updateText = (e: Expression): Expression => {
 *   // Apply the transformation to expressions you care about.
 *   return matchExpression({
 *     Text: val => {
 *       const newText = 'New text';
 *       // Note, this is the `Text` expression not the String `Element` which contains a `Text` element.
 *       return EText(newText);
 *     },
 *     _: identity
 *   })(e)
 * }
 * const updatedComponentRender = everywhere(updateText)(component.render);
 * ```
 */
export const _everywhere = weakMemo(
  <e>(
    onExpression: (
      arg1: (arg1: ExpressionF<e>) => ExpressionF<e>
    ) => (arg1: e) => e
  ): ((arg1: (arg1: e) => e) => (arg1: e) => e) =>
    weakMemo((fn: (arg1: e) => e): ((arg1: e) => e) =>
      weakMemo((tree: e): e => {
        const result = fn(tree);
        const next = map(_everywhere(onExpression)(fn));
        return onExpression(next)(result);
      })
    )
);

/**
 * Like `everywhere` but allows the updater function to short-circuit
 * traversal of a subtree by returning a `Stoppable`. Returning
 * `stop(result)` will apply `result` at the current node without
 * recursing into its children, while `resume(result)` behaves like
 * regular `everywhere` and continues walking into the subtree.
 *
 * ```jsx
 * // Rewrite Text expressions everywhere, but skip the subtree of any
 * // Element already tagged as `pre-rendered` so its nested children
 * // (record, list, and inner elements) are left untouched.
 * const rewriteOutsideRenderedElements = (
 *   e: Expression
 * ): Stoppable<Expression> =>
 *   matchExpression({
 *     Text: () => resume(EText('New text')),
 *     Element: (el) =>
 *       el.id === 'pre-rendered' ? stop(e) : resume(e),
 *     _: () => resume(e),
 *   })(e);
 *
 * const updatedTree =
 *   everywhereStoppable(rewriteOutsideRenderedElements)(tree);
 * ```
 */
export const _everywhereStoppable = weakMemo(
  <e>(
    onExpression: (
      arg1: (arg1: ExpressionF<e>) => ExpressionF<e>
    ) => (arg1: e) => e
  ): ((arg1: (arg1: e) => Stoppable<e>) => (arg1: e) => e) =>
    weakMemo((fn: (arg1: e) => Stoppable<e>): ((arg1: e) => e) =>
      weakMemo((tree: e): e => {
        const updated = fn(tree);
        const result = getFirst(updated);
        const shouldStop = getSecond(updated);
        if (shouldStop) {
          return result;
        }
        const next = map(_everywhereStoppable(onExpression)(fn));
        return onExpression(next)(result);
      })
    )
);

export type EverywhereWithContextMapper<ContextType, ResultType> = (
  arg1: ResultType
) => (arg1: ContextType) => Pair<ContextType, ResultType>;

/**
 * Like `everywhere` but allows you to provide ancestral context during your
 * mapping and updating phase.
 *
 * See the documentation for `everything`.
 *
 * Generics:
 * c = ContextType
 * e = Expression
 */
export const _everywhereWithContext = weakMemo(
  <e>(
    onExpression: (
      arg1: (arg1: ExpressionF<e>) => ExpressionF<e>
    ) => (arg1: e) => e
  ): (<c>(
    arg1: EverywhereWithContextMapper<c, e>
  ) => (arg1: c, arg2: e) => e) =>
    weakMemo(
      <c>(
        updateFn: EverywhereWithContextMapper<c, e>
      ): ((arg1: c, arg2: e) => e) =>
        (context: c, tree: e): e => {
          const updated = updateFn(tree)(context);
          const nextContext = getFirst(updated);
          const result = getSecond(updated);

          const next = map((expression) =>
            _everywhereWithContext(onExpression)(updateFn)(
              nextContext,
              // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'e'.
              expression
            )
          );
          return onExpression(next)(result);
        }
    )
);

const returnResult = constantIdentity;

type MatcherResultType<s, e> = (arg1: Pair<s, e>) => Pair<s, e>;
type UpdaterFnType<s, e> = (arg1: e) => State<s, e>;

const everywhereWithStateMatcher = weakMemo(
  <s, e>(
    lens: Lens<e, ExpressionF<e>>
  ): ((
    arg1: UpdaterFnType<s, e>
  ) => (arg1: ExpressionF<e>) => MatcherResultType<s, e>) =>
    weakMemo(
      (
        updaterFn: UpdaterFnType<s, e>
      ): ((arg1: ExpressionF<e>) => MatcherResultType<s, e>) =>
        matchExpression<e, MatcherResultType<s, e>>({
          Record: (properties) => (result: Pair<s, e>) => {
            const state = getFirst(result);
            const expr = getSecond(result);

            const initialResult = pair(state, emptyObject);

            let valuesChanged = false;

            const updatedResult = objectKeys(properties).reduce((acc, key) => {
              const value = properties[key];

              const updatedValueResult = _everywhereWithState(lens)(updaterFn)(
                // @ts-expect-error - TS2345 - Argument of type 'e | undefined' is not assignable to parameter of type 'e'.
                value
              )(getFirst(acc));

              const updatedValueState = getFirst(updatedValueResult);
              const updatedValueExpr = getSecond(updatedValueResult);

              if (updatedValueExpr !== value) {
                valuesChanged = true;
              }

              return pair(updatedValueState, {
                ...getSecond(acc),
                [key]: updatedValueExpr,
              });
            }, initialResult);

            const updatedState = getFirst(updatedResult);
            const updatedExpr = getSecond(updatedResult);

            if (!valuesChanged) {
              if (state === updatedState) {
                return pair(state, expr);
              }

              return pair(updatedState, expr);
            }

            return pair(updatedState, lens.set(_ERecord(updatedExpr))(expr));
          },
          Extend:
            ({record, value, prop}) =>
            (result: Pair<s, e>) => {
              const state = getFirst(result);
              const expr = getSecond(result);

              const updatedRecordResult =
                _everywhereWithState(lens)(updaterFn)(record)(state);

              const updatedRecordState = getFirst(updatedRecordResult);
              const updatedRecordExpr = getSecond(updatedRecordResult);

              const updatedValueResult =
                _everywhereWithState(lens)(updaterFn)(value)(
                  updatedRecordState
                );

              const updatedValueState = getFirst(updatedValueResult);
              const updatedValueExpr = getSecond(updatedValueResult);
              if (record === updatedRecordExpr && value === updatedValueExpr) {
                if (state === updatedValueState) {
                  return result;
                }
                return pair(updatedValueState, expr);
              }

              // return new Extend thing
              return pair(
                updatedValueState,
                lens.set(
                  _EExtend({
                    record: updatedRecordExpr,
                    prop,
                    value: updatedValueExpr,
                  })
                )(expr)
              );
            },
          List: (list) => (result: Pair<s, e>) => {
            const state = getFirst(result);
            const expr = getSecond(result);

            const initialResult = pair(state, emptyArray);

            let itemsChanged = false;

            const updatedResult = list.reduce((acc, item) => {
              const updatedItemResult = _everywhereWithState(lens)(updaterFn)(
                item
              )(getFirst(acc));

              const updatedItemState = getFirst(updatedItemResult);
              const updatedItemExpr = getSecond(updatedItemResult);

              if (updatedItemExpr !== item) {
                itemsChanged = true;
              }
              return pair(updatedItemState, [
                ...getSecond(acc),
                updatedItemExpr,
              ]);
            }, initialResult);

            const updatedState = getFirst(updatedResult);
            const updatedExpr = getSecond(updatedResult);

            if (!itemsChanged) {
              if (state === updatedState) {
                return result;
              }
              return pair(updatedState, expr);
            }

            return pair(updatedState, lens.set(_EList(updatedExpr))(expr));
          },
          Union: (union) => (result: Pair<s, e>) => {
            const state = getFirst(result);
            const expr = getSecond(result);

            const updatedResult = _everywhereWithState(lens)(updaterFn)(
              union.value
            )(state);

            const updatedState = getFirst(updatedResult);
            const updatedExpr = getSecond(updatedResult);

            if (union.value === updatedExpr) {
              if (state === updatedState) {
                return result;
              }
              return pair(updatedState, expr);
            }

            return pair(
              updatedState,
              lens.set(
                _EUnion({
                  tag: union.tag,
                  value: updatedExpr,
                })
              )(expr)
            );
          },
          Case:
            ({cases, value}) =>
            (result: Pair<s, e>) => {
              const state = getFirst(result);
              const expr = getSecond(result);

              const updatedValueResult =
                _everywhereWithState(lens)(updaterFn)(value)(state);

              const updatedValueState = getFirst(updatedValueResult);
              const updatedValueExpr = getSecond(updatedValueResult);

              const initialResult = pair(updatedValueState, emptyObject);

              let casesChanged = false;

              const updatedCasesResult = objectKeys(cases).reduce(
                (acc, key) => {
                  const handler = cases[key];
                  const updatedHandlerResult = _everywhereWithState(lens)(
                    updaterFn
                    // @ts-expect-error - TS2345 - Argument of type 'e | undefined' is not assignable to parameter of type 'e'.
                  )(handler)(getFirst(acc));

                  const updatedHandlerState = getFirst(updatedHandlerResult);
                  const updatedHandlerExpr = getSecond(updatedHandlerResult);

                  if (updatedHandlerExpr !== handler) {
                    casesChanged = true;
                  }
                  return pair(updatedHandlerState, {
                    ...getSecond(acc),
                    [key]: updatedHandlerExpr,
                  });
                },
                initialResult
              );

              const updatedCasesState = getFirst(updatedCasesResult);
              const updatedCasesExpr = getSecond(updatedCasesResult);

              if (!casesChanged) {
                if (value === updatedValueExpr) {
                  if (state === updatedValueState) {
                    return result;
                  }
                  return pair(updatedCasesState, expr);
                }
                return pair(
                  updatedCasesState,
                  lens.set(
                    _ECase({
                      value: updatedValueExpr,
                      cases,
                    })
                  )(expr)
                );
              }

              return pair(
                updatedCasesState,
                lens.set(
                  _ECase({
                    value: updatedValueExpr,
                    cases: updatedCasesExpr,
                  })
                )(expr)
              );
            },
          Element: (el) => (result: Pair<s, e>) => {
            const state = getFirst(result);
            const expr = getSecond(result);

            const data = _getElementData(el);

            const updatedResult =
              _everywhereWithState(lens)(updaterFn)(data)(state);

            const updatedState = getFirst(updatedResult);
            const updatedExpr = getSecond(updatedResult);

            if (data === updatedExpr) {
              if (state === updatedState) {
                return result;
              }
              return pair(updatedState, expr);
            }

            return pair(
              updatedState,
              lens.set(_EElement(_setElementData(el, updatedExpr)))(expr)
            );
          },
          Call:
            ({fun, arg}) =>
            (result: Pair<s, e>) => {
              const state = getFirst(result);
              const expr = getSecond(result);

              const updatedFunResult =
                _everywhereWithState(lens)(updaterFn)(fun)(state);

              const updatedFunState = getFirst(updatedFunResult);
              const updatedFunExpr = getSecond(updatedFunResult);

              const updatedArgResult =
                _everywhereWithState(lens)(updaterFn)(arg)(updatedFunState);

              const updatedArgState = getFirst(updatedArgResult);
              const updatedArgExpr = getSecond(updatedArgResult);

              if (fun === updatedFunExpr && arg === updatedArgExpr) {
                if (state === updatedArgState) {
                  return result;
                }
                return pair(updatedArgState, expr);
              }

              // return new Call thing
              return pair(
                updatedArgState,
                lens.set(
                  _ECall({
                    fun: updatedFunExpr,
                    arg: updatedArgExpr,
                  })
                )(expr)
              );
            },
          Function: (fun) => (result: Pair<s, e>) => {
            const state = getFirst(result);
            const expr = getSecond(result);

            const updatedBodyResult = _everywhereWithState(lens)(updaterFn)(
              fun.body
            )(state);

            const updatedBodyState = getFirst(updatedBodyResult);
            const updatedBodyExpr = getSecond(updatedBodyResult);

            if (fun.body === updatedBodyExpr) {
              if (state === updatedBodyState) {
                return result;
              }
              return pair(updatedBodyState, expr);
            }

            return pair(
              updatedBodyState,
              lens.set(
                _EFunction({
                  param: fun.param,
                  body: updatedBodyExpr,
                })
              )(expr)
            );
          },
          Select: (sel) => (result: Pair<s, e>) => {
            const state = getFirst(result);
            const expr = getSecond(result);

            const updatedResult = _everywhereWithState(lens)(updaterFn)(
              sel.from
            )(state);

            const updatedState = getFirst(updatedResult);
            const updatedExpr = getSecond(updatedResult);

            if (sel.from === updatedExpr) {
              if (state === updatedState) {
                return result;
              }
              return pair(updatedState, expr);
            }

            return pair(
              updatedState,
              lens.set(
                _ESelect({
                  from: updatedExpr,
                  prop: sel.prop,
                })
              )(expr)
            );
          },
          Typed: (typed) => (result: Pair<s, e>) => {
            const state = getFirst(result);
            const expr = getSecond(result);

            const updatedResult = _everywhereWithState(lens)(updaterFn)(
              typed.expr
            )(state);

            const updatedState = getFirst(updatedResult);
            const updatedExpr = getSecond(updatedResult);

            if (typed.expr === updatedExpr) {
              if (state === updatedState) {
                return result;
              }
              return pair(updatedState, expr);
            }

            return pair(
              updatedState,
              lens.set(
                _ETyped({
                  type: typed.type,
                  expr: updatedExpr,
                })
              )(expr)
            );
          },
          Distinct:
            ({name, value}) =>
            (result: Pair<s, e>) => {
              const state = getFirst(result);
              const expr = getSecond(result);

              const updatedResult =
                _everywhereWithState(lens)(updaterFn)(value)(state);

              const updatedState = getFirst(updatedResult);
              const updatedExpr = getSecond(updatedResult);

              if (value === updatedExpr) {
                if (state === updatedState) {
                  return result;
                }
                return pair(updatedState, expr);
              }

              return pair(
                updatedState,
                lens.set(
                  _EDistinct({
                    name,
                    value: updatedExpr,
                  })
                )(expr)
              );
            },
          _: returnResult,
        })
    )
);

export type Lens<s, e> = {
  get: (arg1: s) => e;

  set: (arg1: e) => (arg1: s) => s;
};
/**
 * Like `everywhere` but has a mechanism to keep track of state during your mapping and updating phase.
 *
 * Generics:
 * s = State
 * e = Expression
 */
export const _everywhereWithState = <e>(
  lens: Lens<e, ExpressionF<e>>
): (<s>(arg1: UpdaterFnType<s, e>) => (arg1: e) => State<s, e>) =>
  weakMemo(
    <s>(updaterFn: UpdaterFnType<s, e>): ((arg1: e) => State<s, e>) =>
      (currentExpr: e) =>
      (currentState: s): Pair<s, e> => {
        // We invoke the provided `updaterFn` with the current [state, expr]. Each
        // iteration of `everywhereWithState` will provide a new [state, state] Pair
        // to iterate over.
        const updatedResult = updaterFn(currentExpr)(currentState);

        const exprF = lens.get(getSecond(updatedResult));

        // We use `matchExpression` to update and return a new [state, expr] Pair.
        // These cases are the types that could have sub-expressions we need to
        // recurse over. For types that don't contain sub-expressions, we just return
        // the result directly with `returnResult` as there is nothing to recurse
        // over.
        return everywhereWithStateMatcher<s, e>(lens)(updaterFn)(exprF)(
          updatedResult
        );
      }
  );

type ReduceExpressionFunction = (
  expression: ExpressionF<any>
) => <T, C>(combine: (acc: T, child: C) => T, init: T) => T;

const reduceExpressionChildren: ReduceExpressionFunction = matchExpression({
  Record: (record) => (combine, init) => {
    let acc = init;
    for (const k in record) acc = combine(acc, record[k]!);
    return acc;
  },
  Extend:
    ({record, value}) =>
    (combine, init) =>
      combine(combine(init, record), value),
  Select: (sel) => (combine, init) => combine(init, sel.from),
  List: (list) => (combine, init) => {
    let acc = init;
    for (const item of list) acc = combine(acc, item);
    return acc;
  },
  Union: (union) => (combine, init) => combine(init, union.value),
  Case:
    ({value, cases}) =>
    (combine, init) => {
      let acc = combine(init, value);
      for (const k in cases) acc = combine(acc, cases[k]!);
      return acc;
    },
  Element: (el) => (combine, init) => combine(init, _getElementData(el)),
  Call:
    ({fun, arg}) =>
    (combine, init) =>
      combine(combine(init, fun), arg),
  Variable: constant((_, init) => init),
  Function: (fun) => (combine, init) => combine(init, fun.body),
  Typed: (typed) => (combine, init) => combine(init, typed.expr),
  Distinct: (distinct) => (combine, init) => combine(init, distinct.value),
  _: constant((_, init) => init),
});

export type Stoppable<a> = Pair<a, boolean>;

/**
 * Used to signal to `everything` that it should not traverse this element's children.
 */
export const _stop = <a>(v: a): Stoppable<a> => pair(v, true);

/**
 * Used to signal to `everything` that it should continue to traverse this element's children.
 */
export const _resume = <a>(v: a): Stoppable<a> => pair(v, false);

export type ResultCombiner<ResultType> = (
  arg1: ResultType
) => (arg1: ResultType) => ResultType;
type EverythingMapper<ResultType, a> = (arg1: a) => Stoppable<ResultType>;
export type Everything<ResultType, a> = (arg1: a) => ResultType;

/**
 * Applies `fn` to the given Expression then recurses into its sub-Expressions
 * and does the same accumulating results using the `combine` function.
 * The return value of `fn` needs to be wrapped in a `Stoppable`. Returning
 * `stop(result)` will stop `everything` from walking the rest of this branch,
 * while `resume(result)` tells it to continue the walk.
 * <pre>
 * +--------------------------------+
 * |            Problem             |
 * +--------------------------------+
 *       |                |
 *       |                |
 *       v                v
 * +---------------++---------------+
 * | Sub Problem 1 || Sub Problem 2 |
 * +---------------++---------------+
 *       |                |
 *       | Divide         | Divide    <-- Everything will divide the work for you by calling your EverythingMapper
 *       v                v               which allows you to map the Expression to a Stoppable<ResultType>.
 * +---------------++---------------+
 * |  Solution 1   ||  Solution 2   | <-- You provide an EverythingMapper that takes an Expression & returns a ResultType.
 * +---------------++---------------+
 *       |                |
 *       | Combine        | Combine   <-- You provide a ResultCombiner which everything uses to combine
 *       v                v               your ResultTypes into a single ResultType.
 * +--------------------------------+
 * |            Solution            |
 * +--------------------------------+
 * </pre>
 *
 * Generics:
 * r = ResultType
 * e = Expression
 */
export const _everything = weakMemo(
  <e>(
    getExpression: (arg1: e) => ExpressionF<e>
  ): (<r>(
    arg1: ResultCombiner<r>
  ) => (arg1: EverythingMapper<r, e>) => Everything<r, e>) =>
    weakMemo(
      <r>(
        combine: ResultCombiner<r>
      ): ((arg1: EverythingMapper<r, e>) => Everything<r, e>) =>
        weakMemo((fn: EverythingMapper<r, e>): Everything<r, e> => {
          function combineAll(currentResult: r, nextExpr: e) {
            const nextResult =
              _everything(getExpression)(combine)(fn)(nextExpr);
            return combine(currentResult)(nextResult);
          }

          return weakMemo(function getExpCallback(exprLike: e): r {
            const updated = fn(exprLike);
            const result = getFirst(updated);
            const shouldStop = getSecond(updated);

            if (shouldStop) return result;
            const expr = getExpression(exprLike);
            return reduceExpressionChildren(expr)(combineAll, result);
          });
        })
    )
);

export type EverythingWithContextMapper<ContextType, ReturnType, e> = (
  arg1: e
) => State<ContextType, ReturnType>;

/**
 * Behaves similarly to `everything` by walking the tree & calling your `EverythingWithContextMapper`
 * function, but with the slight difference of allowing you to return the `ContextType` you want provided to descendant expressions
 * as well as return a `ResultType` that will eventually be combined by your `ResultCombiner`.
 *
 * `everythingWithContext` differs from `everything` by allowing you to provide ancestral context during your
 * mapping phase and `everythingWithContext` does not support `Stoppable`.
 *
 * See the documentation for `everything`.
 *
 * Generics:
 * c = ContextType
 * r = ResultType
 * e = Expression
 */
export const _everythingWithContext = weakMemo(
  <e>(
    getExpression: (arg1: e) => ExpressionF<e>
  ): (<c>(
    arg1: c
  ) => <r>(
    arg1: ResultCombiner<r>
  ) => (arg1: EverythingWithContextMapper<c, r, e>) => Everything<r, e>) =>
    weakMemo(
      <c>(
        context: c
      ): (<r>(
        arg1: ResultCombiner<r>
      ) => (arg1: EverythingWithContextMapper<c, r, e>) => Everything<r, e>) =>
        weakMemo(
          <r>(
            combine: ResultCombiner<r>
          ): ((
            arg1: EverythingWithContextMapper<c, r, e>
          ) => Everything<r, e>) =>
            weakMemo(
              (
                updaterFn: EverythingWithContextMapper<c, r, e>
              ): Everything<r, e> => {
                const combineAll =
                  (contextForCombination: c) =>
                  (currentResult: r, nextExpr: e) => {
                    const nextResult = _everythingWithContext(getExpression)(
                      contextForCombination
                    )(combine)(updaterFn)(nextExpr);

                    return combine(currentResult)(nextResult);
                  };

                return weakMemo((exprLike: e): r => {
                  const updated = updaterFn(exprLike)(context);
                  const nextContext = getFirst(updated);
                  const nextResult = getSecond(updated);
                  const expr = getExpression(exprLike);

                  return reduceExpressionChildren(expr)(
                    combineAll(nextContext),
                    nextResult
                  );
                });
              }
            )
        )
    )
);

export const _isDataVariable: <r>(arg1: ExpressionF<r>) => boolean =
  matchExpression({
    Variable: (val) => val === 'data',
    _: constantFalse,
  });

export const _isElement: <r>(arg1: ExpressionF<r>) => boolean = matchExpression(
  {
    Element: constantTrue,
    _: constantFalse,
  }
);

export const _isRecord: <r>(arg1: ExpressionF<r>) => boolean = matchExpression({
  Record: constantTrue,
  _: constantFalse,
});

export const _isHole: <r>(arg1: ExpressionF<r>) => boolean = matchExpression({
  Hole: constantTrue,
  _: constantFalse,
});

export const _isList: <r>(arg1: ExpressionF<r>) => boolean = matchExpression({
  List: constantTrue,
  _: constantFalse,
});

export const _isVariable: <r>(arg1: ExpressionF<r>) => boolean =
  matchExpression({
    Variable: constantTrue,
    _: constantFalse,
  });

/**
 * `isBinding` returns true if the expression is a non-literal.
 * A non-literal is an expression that requires reading values from the
 * context to be evaluated.
 */
export const _isBinding: <r>(arg1: ExpressionF<r>) => boolean = matchExpression(
  {
    Variable: constantTrue,
    Select: constantTrue,
    Call: constantTrue,
    _: constantFalse,
  }
);

export const _isEnum: <r>(arg1: ExpressionF<r>) => boolean = matchExpression({
  Enum: constantTrue,
  _: constantFalse,
});

export const _isText: <r>(arg1: ExpressionF<r>) => boolean = matchExpression({
  Text: constantTrue,
  _: constantFalse,
});

export const _isSelect: <r>(arg1: ExpressionF<r>) => boolean = matchExpression({
  Select: constantTrue,
  _: constantFalse,
});

export const _isNumber: <r>(arg1: ExpressionF<r>) => boolean = matchExpression({
  Number: constantTrue,
  _: constantFalse,
});

export const _isCall: <r>(arg1: ExpressionF<r>) => boolean = matchExpression({
  Call: constantTrue,
  _: constantFalse,
});

export const _isFunction: <r>(arg1: ExpressionF<r>) => boolean =
  matchExpression({
    Function: constantTrue,
    _: constantFalse,
  });

type DeconstructedCall<a> = {
  fun: a;
  args: Array<a>;
};

/**
 * Splits an expression viewed as 0 or more nested Calls into an expression
 * representing the function and an array of expressions representing
 * the arguments.
 *
 * It helps to easily get at the function or arguments of an expression.
 */
export const _deconstructCall = <a>(
  getExpression: (arg1: a) => ExpressionF<a>
): ((arg1: a) => DeconstructedCall<a>) => {
  const go = (thing: a): DeconstructedCall<a> => {
    const expr = getExpression(thing);
    return deconstructCallHandler(expr)(go, thing);
  };
  return go;
};
const deconstructCallHandler = matchExpression({
  Call: (val) => (go: any, _: any) => {
    const inner = go(val.fun);
    return {
      fun: inner.fun,
      args: append(inner.args, val.arg),
    };
  },
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'thing' implicitly has an 'any' type.
  _: constant((_, thing) => ({fun: thing, args: emptyArray})),
}) as any;

type DeconstructedFunc<a> = {
  body: a;
  params: Array<Identifier>;
};

/**
 * Splits an expression viewed as 0 or more Function expressions into an expression
 * representing the body and an array of params
 */
export const _deconstructFunc = <a>(
  getExpression: (arg1: a) => ExpressionF<a>
): ((arg1: a) => DeconstructedFunc<a>) => {
  const go = (thing: a): DeconstructedFunc<a> => {
    const expr = getExpression(thing);
    return deconstructFuncHandler(expr)(go, thing);
  };
  return go;
};

const deconstructFuncHandler = matchExpression({
  Function: (val) => (go: any, _: any) => {
    const inner = go(val.body);
    return {
      body: inner.body,
      params: prepend(val.param, inner.params),
    };
  },
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'thing' implicitly has an 'any' type.
  _: constant((_, thing) => ({body: thing, params: emptyArray})),
}) as any;
